"use client"

/* eslint-disable @next/next/no-img-element -- official network logo asset
   files from public/icons/social (real brand marks, colors baked into the
   SVG files), the same renderer ShareBar uses. */

/**
 * ProductImageShareSheet — the share control on the PDP hero image
 * (FRIDGE-344, owner 08/17/2026: "the share button on product pages
 * (specifically the one on the product image) needs to kind of open up nice
 * showing options to share, copy, email, instagram, etc").
 *
 * The round white button that used to copy the link silently now opens a
 * light, on-brand sheet anchored under it: Copy link (with a confirmed
 * state), Email, X, Facebook, Pinterest (with the product image), WhatsApp,
 * and Instagram — which has no web share URL, so that row hands off to the
 * device share sheet (navigator.share) when the browser has one and otherwise
 * copies the link and says so.
 *
 * Rules kept here:
 *  - The icons are the SAME official full-color marks the sitewide ShareBar
 *    renders (SOCIAL_ICON_SRC) — no chips, never greyed, the logo is the
 *    button (owner spec 07/20).
 *  - EVERY share URL is a fridge.com URL: links are built from the canonical
 *    product path on the fridge.com origin, never from wherever the page
 *    happens to be served (preview deployments, www).
 *  - Every control's title and aria-label says WHAT is shared and carries the
 *    Fridge.com brand (FRIDGE-352), built by the same shareControlMeta the
 *    ShareBar uses so the two can never drift.
 *  - Keyboard: the trigger is a dialog trigger (aria-haspopup / aria-expanded /
 *    aria-controls); opening moves focus to the first option; Escape or a
 *    click outside closes and returns focus to the trigger; ArrowUp/ArrowDown
 *    move between options.
 *  - Light styling only, Fridge.com palette.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Check, Link2, Mail, Share2, X as CloseIcon } from 'lucide-react'
import { SOCIAL_ICON_SRC, shareControlMeta } from '@/components/data/share-bar'
import { useIsHydrated } from '@/hooks/use-is-hydrated'
import { trackPinterestSaveInitiated } from '@/lib/pinterest-save-analytics'
import { pinterestShareUrl } from '@/lib/social-share-url'

/** The one origin a Fridge.com share link may point at. */
export const FRIDGE_ORIGIN = 'https://fridge.com'

export interface ProductShareTarget {
  /** Display name of the product (the H1 text). */
  name: string
  /** Canonical product path, e.g. "/products/lg-lrfxs2503s-fin0000110". A full URL is accepted and re-homed. */
  path: string
  /** Hero image, absolute URL or a fridge.com path — used for the Pinterest pin. */
  imageUrl?: string | null
}

/**
 * Re-home any path or URL onto https://fridge.com, keeping path, query and
 * hash. A relative path becomes an absolute fridge.com URL; a URL on another
 * host (a preview deployment, www.fridge.com) becomes the same page on
 * fridge.com. This is what makes "every share URL is a fridge.com URL" true
 * by construction rather than by review.
 */
export function toFridgeUrl(pathOrUrl: string): string {
  const raw = (pathOrUrl ?? '').trim() || '/'
  let u: URL
  try {
    u = /^https?:\/\//i.test(raw) ? new URL(raw) : new URL(raw.startsWith('/') ? raw : `/${raw}`, FRIDGE_ORIGIN)
  } catch {
    u = new URL('/', FRIDGE_ORIGIN)
  }
  const home = new URL(FRIDGE_ORIGIN)
  home.pathname = u.pathname
  home.search = u.search
  home.hash = u.hash
  return home.toString()
}

/** The share copy: the product name with the brand exactly once. */
export function productShareText(name: string): string {
  const clean = (name ?? '').trim()
  if (!clean) return 'Fridge.com'
  return /fridge\.com\s*$/i.test(clean) ? clean : `${clean} — Fridge.com`
}

/**
 * The fridge.com URL a given network receives — canonical path plus the
 * measurable UTM set (source = network, medium = social, campaign = product,
 * content = gallery-share). `network` is the utm_source, e.g. "x".
 */
export function productShareUrl(path: string, network: string): string {
  const u = new URL(toFridgeUrl(path))
  u.searchParams.set('utm_source', network)
  u.searchParams.set('utm_medium', 'social')
  u.searchParams.set('utm_campaign', 'product')
  u.searchParams.set('utm_content', 'gallery-share')
  return u.toString()
}

export interface ProductShareLinks {
  /** The bare canonical fridge.com URL — what "Copy link" copies. */
  copy: string
  /** mailto: with a subject and body that name the product and Fridge.com. */
  email: string
  x: string
  facebook: string
  pinterest: string
  whatsapp: string
}

/** Pure builders for every link the sheet can open. Tested in jest. */
export function buildProductShareLinks(target: ProductShareTarget): ProductShareLinks {
  const text = productShareText(target.name)
  const copy = toFridgeUrl(target.path)
  const emailBody = `I found this on Fridge.com and thought you'd want to see it:\n\n${text}\n${productShareUrl(target.path, 'email')}`
  return {
    copy,
    email: `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(emailBody)}`,
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(productShareUrl(target.path, 'x'))}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productShareUrl(target.path, 'facebook'))}`,
    pinterest: pinterestShareUrl(productShareUrl(target.path, 'pinterest'), text, target.imageUrl || undefined),
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${text} ${productShareUrl(target.path, 'whatsapp')}`)}`,
  }
}

function toast(msg: string) {
  if (typeof document === 'undefined') return
  const el = document.createElement('div')
  el.textContent = msg
  el.setAttribute('role', 'status')
  el.className = 'fixed bottom-4 right-4 bg-[#0469f0] text-white text-[13px] font-bold px-4 py-2 rounded-lg shadow-lg z-50'
  document.body.appendChild(el)
  setTimeout(() => {
    if (document.body.contains(el)) document.body.removeChild(el)
  }, 2200)
}

interface ProductImageShareSheetProps extends ProductShareTarget {
  className?: string
  /** Render with the sheet already open (server-render tests). Default false. */
  defaultOpen?: boolean
}

const BRAND_ORANGE = '#ff9951'

export function ProductImageShareSheet({ name, path, imageUrl, className = '', defaultOpen = false }: ProductImageShareSheetProps) {
  const [open, setOpen] = useState(defaultOpen)
  const [copied, setCopied] = useState(false)
  const [instagramHint, setInstagramHint] = useState(false)
  // Capability probe, DERIVED after hydration rather than pushed into state
  // from an effect (react-hooks/set-state-in-effect). false during the server
  // render and the hydration pass, exactly as before, so the markup matches.
  const hydrated = useIsHydrated()
  const canNative = hydrated && typeof navigator !== 'undefined' && 'share' in navigator
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const firstOptionRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()

  const close = useCallback((restoreFocus = true) => {
    setOpen(false)
    setInstagramHint(false)
    if (restoreFocus) triggerRef.current?.focus()
  }, [])

  // Escape closes; a pointer-down outside the sheet closes (without stealing focus).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close(true)
        return
      }
      // Roving focus between the share options. This lives on `document`
      // beside the Escape handler rather than on the panel as a JSX
      // `onKeyDown`, for two reasons: a `role="dialog"` element is
      // non-interactive, so a key listener stranded on it is a real
      // accessibility smell (jsx-a11y/no-noninteractive-element-interactions);
      // and this way the arrows work from anywhere inside the open sheet
      // rather than only when the panel node itself has focus.
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      const panel = rootRef.current?.querySelector<HTMLElement>('[data-testid="pdp-image-share-sheet"]')
      if (!panel || !panel.contains(e.target as Node)) return
      const items = Array.from(panel.querySelectorAll<HTMLElement>('[data-share-option]'))
      if (items.length === 0) return
      const idx = items.indexOf(document.activeElement as HTMLElement)
      const next = e.key === 'ArrowDown' ? (idx + 1) % items.length : (idx - 1 + items.length) % items.length
      items[next]?.focus()
      e.preventDefault()
    }
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('touchstart', onPointer)
    // Focus lands on the first option once the panel is in the DOM.
    const t = window.setTimeout(() => firstOptionRef.current?.focus(), 0)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('touchstart', onPointer)
      window.clearTimeout(t)
    }
  }, [open, close])

  const subject = `the ${(name || 'product').trim()}`
  const links = buildProductShareLinks({ name, path, imageUrl })
  const shareText = productShareText(name)

  const openWin = (u: string) => {
    window.open(u, '_blank', 'noopener,noreferrer,width=620,height=560')
    close(false)
  }

  const copyLink = async (): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(links.copy)
      setCopied(true)
      toast('Fridge.com link copied')
      window.setTimeout(() => setCopied(false), 1800)
      return true
    } catch {
      toast('Copy failed')
      return false
    }
  }

  const shareToInstagram = async () => {
    // Instagram has no web share URL. Hand off to the device share sheet when
    // the browser has one (Instagram appears there on phones); otherwise copy
    // the fridge.com link and say what to do with it.
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> }
    if (typeof nav.share === 'function') {
      try {
        await nav.share({ title: shareText, text: shareText, url: productShareUrl(path, 'instagram') })
        close(false)
      } catch {
        /* user cancelled */
      }
      return
    }
    const ok = await copyLink()
    setInstagramHint(ok)
  }

  // Arrow keys move between the options inside the panel.
  const row =
    'w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-semibold text-gray-800 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9951] cursor-pointer'
  const iconCls = 'w-[22px] h-[22px] shrink-0'

  const triggerMeta = `Share ${subject} — Fridge.com`
  const copyMeta = copied ? `Fridge.com link copied — ${subject}` : shareControlMeta(subject, 'copy')
  const emailMeta = shareControlMeta(subject, 'email')
  const instagramMeta = canNative
    ? `Share ${subject} to Instagram or another app — Fridge.com`
    : `Copy the Fridge.com link to ${subject} for Instagram`
  const closeMeta = `Close the Fridge.com share menu`

  const networks: Array<{ key: 'x' | 'facebook' | 'pinterest' | 'whatsapp'; label: string; href: string }> = [
    { key: 'x', label: 'X', href: links.x },
    { key: 'facebook', label: 'Facebook', href: links.facebook },
    { key: 'pinterest', label: 'Pinterest', href: links.pinterest },
    { key: 'whatsapp', label: 'WhatsApp', href: links.whatsapp },
  ]

  return (
    <div ref={rootRef} className={`absolute top-3 right-3 sm:top-4 sm:right-4 z-10 ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close(true) : setOpen(true))}
        className="p-2.5 bg-white rounded-full shadow-md hover:bg-gray-50 min-w-[44px] min-h-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9951]"
        title={triggerMeta}
        aria-label={triggerMeta}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        data-testid="pdp-image-share-trigger"
      >
        {/* Brand-orange ink at rest (owner spec 07/20: share controls are
            always colorful, never grey). */}
        <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#ff9951]" />
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label={triggerMeta}
          // A dialog that handles Escape must be able to RECEIVE the key event,
          // which means it must be focusable. tabIndex={-1} makes it
          // programmatically focusable without adding it to the tab order —
          // the standard shape for a dialog container, and it is also what
          // makes the keydown handler legitimate here rather than a listener
          // stranded on a non-interactive node.
          tabIndex={-1}
          className="absolute right-0 top-[calc(100%+0.5rem)] z-20 w-60 rounded-xl border border-gray-200 bg-white p-2 shadow-xl"
          data-testid="pdp-image-share-sheet"
        >
          <div className="flex items-center justify-between px-2.5 pt-1 pb-1.5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Share this product</p>
            <button
              type="button"
              onClick={() => close(true)}
              className="rounded-md p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9951]"
              title={closeMeta}
              aria-label={closeMeta}
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>

          <button ref={firstOptionRef} type="button" data-share-option onClick={copyLink} className={row} title={copyMeta} aria-label={copyMeta}>
            {copied ? <Check className={`${iconCls} text-[#0469f0]`} /> : <Link2 className={iconCls} style={{ color: BRAND_ORANGE }} />}
            <span>{copied ? 'Link copied' : 'Copy link'}</span>
          </button>

          <a
            href={links.email}
            data-share-option
            onClick={() => close(false)}
            className={row}
            title={emailMeta}
            aria-label={emailMeta}
          >
            <Mail className={iconCls} style={{ color: BRAND_ORANGE }} />
            <span>Email</span>
          </a>

          {networks.map((n) => {
            const meta = shareControlMeta(subject, 'network', n.label)
            if (n.key === 'pinterest') {
              return (
                <a key={n.key} href={n.href} target="_blank" rel="noopener noreferrer" data-share-option
                  onClick={() => {
                    trackPinterestSaveInitiated(toFridgeUrl(path), 'product', new URL(n.href).searchParams.has('media'))
                    close(false)
                  }}
                  className={row} title={meta} aria-label={meta}>
                  <img src={SOCIAL_ICON_SRC[n.key]} alt="" className={iconCls} loading="lazy" decoding="async" />
                  <span>{n.label}</span>
                </a>
              )
            }
            return (
              <button key={n.key} type="button" data-share-option onClick={() => openWin(n.href)} className={row} title={meta} aria-label={meta}>
                <img src={SOCIAL_ICON_SRC[n.key]} alt="" className={iconCls} loading="lazy" decoding="async" />
                <span>{n.label}</span>
              </button>
            )
          })}

          <button type="button" data-share-option onClick={shareToInstagram} className={row} title={instagramMeta} aria-label={instagramMeta}>
            <img src={SOCIAL_ICON_SRC.instagram} alt="" className={iconCls} loading="lazy" decoding="async" />
            <span>{canNative ? 'Instagram / more…' : 'Instagram'}</span>
          </button>
          {instagramHint && (
            <p className="px-2.5 pb-1 pt-0.5 text-[11px] leading-snug text-gray-500" role="status">
              Instagram has no web share link — the Fridge.com link is copied; paste it into your story or bio.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
